import React, { useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  useActiveTab,
  useSubFormApproval,
  useHcpLicensesListApproval,
  useCurrentStageStatus,
} from '../../../../../hooks';
import ApproveButton from '../../ApproveButton/ApproveButton';
import { FormSectionsLayout, tabIdMatch } from './SharedComponents';
import { isReviewerActionableStatus } from '../../../../../constants/statuses';
import axiosInstance from '../../../../../axiosInstance';
import { getCommentDedupeKey } from '../../../../../utils/commentKeys';
import HcpProfileSectionReviewerPanel from '../../HcpProfileSection/HcpProfileSectionReviewerPanel';

export const FormSectionsNonCustomer = ({
  subForms,
  requestProgressId,
  prevComments,
  requestDetails,
  filledForm,
  requestId,
  handleAddComment,
  requestProgress,
  children,
  handleAction,
  roleName = '',
  formName = null,
  persona = 'admin',
  hasHcpLicensesList = false,
  isSendbackMode = false,
  setComments = () => {},
  loadingComments = false,
  registerSendbackBaseline = () => {},
  hcpProfileMenuEnabled = false,
  hcpProfileMainMenuTabs = null,
  renderHcpProfileMenuContent = null,
}) => {
  const [hcpLicensesListComments, setHcpLicensesListComments] = React.useState(
    []
  );
  const [hcpCommentsLoaded, setHcpCommentsLoaded] = React.useState(false);
  const [commentsRefetchKey, setCommentsRefetchKey] = React.useState(0);

  const [profileSectionComments, setProfileSectionComments] = React.useState(
    []
  );
  const [profileCommentsLoaded, setProfileCommentsLoaded] =
    React.useState(false);
  const [approvedProfileSectionKeys, setApprovedProfileSectionKeys] =
    React.useState([]);
  const [profileApproveBusy, setProfileApproveBusy] = React.useState({
    approving: false,
    deleting: false,
  });

  const hcpListRow = requestDetails?.hcpLicensesList;

  const {
    isApproved: isHcpLicensesListApproved,
    approveHcpLicensesList,
    deleteApproval: deleteHcpLicensesListApproval,
  } = useHcpLicensesListApproval({
    hcpLicensesListId: hcpListRow?.id,
    requestId: requestId,
    persona: null,
  });

  React.useEffect(() => {
    let cancelled = false;

    const fetchHcpComments = async () => {
      if (!hcpListRow?.id) {
        if (!cancelled) {
          setHcpLicensesListComments([]);
          setHcpCommentsLoaded(true);
        }
        return;
      }
      if (!cancelled) setHcpCommentsLoaded(false);
      try {
        const response = await axiosInstance.get(
          `/api/hcp-licenses-list-comments/${hcpListRow.id}`
        );
        if (!cancelled) {
          setHcpLicensesListComments(response.data || []);
          setHcpCommentsLoaded(true);
        }
      } catch (error) {
        console.error('error fetching License Information comments:', error);
        if (!cancelled) {
          setHcpLicensesListComments([]);
          setHcpCommentsLoaded(true);
        }
      }
    };

    fetchHcpComments();
    return () => {
      cancelled = true;
    };
  }, [hcpListRow?.id, commentsRefetchKey]);

  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!hcpProfileMenuEnabled || !requestProgressId) {
        if (!cancelled) {
          setProfileSectionComments([]);
          setProfileCommentsLoaded(true);
        }
        return;
      }
      if (!cancelled) setProfileCommentsLoaded(false);
      try {
        const endpoint =
          persona === 'enduser'
            ? `/api/hcp-profile-section-comments/by-progress/enduser/${requestProgressId}`
            : `/api/hcp-profile-section-comments/by-progress/${requestProgressId}`;
        const res = await axiosInstance.get(endpoint);
        if (!cancelled) {
          setProfileSectionComments(res.data || []);
        }
      } catch (e) {
        console.error('error fetching HCP profile section comments:', e);
        if (!cancelled) setProfileSectionComments([]);
      } finally {
        if (!cancelled) setProfileCommentsLoaded(true);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [hcpProfileMenuEnabled, requestProgressId, persona, commentsRefetchKey]);

  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (
        !hcpProfileMenuEnabled ||
        !requestProgressId ||
        persona === 'enduser'
      ) {
        if (!cancelled) setApprovedProfileSectionKeys([]);
        return;
      }
      try {
        const res = await axiosInstance.get(
          '/api/approve-hcp-profile-section/check',
          {
            params: { request_progress_id: requestProgressId },
          }
        );
        if (!cancelled) {
          setApprovedProfileSectionKeys(
            res.data?.approved_profile_section_keys || []
          );
        }
      } catch (e) {
        console.error('error fetching HCP profile section approvals:', e);
        if (!cancelled) setApprovedProfileSectionKeys([]);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [hcpProfileMenuEnabled, requestProgressId, persona, commentsRefetchKey]);

  // Parent (e.g. FormApprove) needs request + HCP list comments for send-back / action bar
  React.useEffect(() => {
    if (typeof setComments !== 'function') return;
    const merged = [
      ...(prevComments || []),
      ...(hcpLicensesListComments || []),
      ...(profileSectionComments || []),
    ];
    const seen = new Map();
    merged.forEach((c) => {
      const key = getCommentDedupeKey(c);
      if (!key) return;
      if (!seen.has(key)) seen.set(key, c);
    });
    setComments(Array.from(seen.values()));
  }, [
    prevComments,
    hcpLicensesListComments,
    profileSectionComments,
    setComments,
  ]);

  // One-time baseline for send-back: existing comments when the page finishes loading (not new ones added later)
  React.useEffect(() => {
    if (typeof registerSendbackBaseline !== 'function') return;
    if (
      !requestId ||
      loadingComments ||
      !hcpCommentsLoaded ||
      !profileCommentsLoaded
    )
      return;
    const merged = [
      ...(prevComments || []),
      ...(hcpLicensesListComments || []),
      ...(profileSectionComments || []),
    ];
    const keys = merged.map(getCommentDedupeKey).filter(Boolean);
    registerSendbackBaseline(keys);
  }, [
    requestId,
    loadingComments,
    hcpCommentsLoaded,
    profileCommentsLoaded,
    prevComments,
    hcpLicensesListComments,
    profileSectionComments,
    registerSendbackBaseline,
  ]);

  // callback to refetch comments after adding a new one
  const handleAddCommentWrapper = useCallback(
    async (...args) => {
      // call the original handleAddComment
      if (handleAddComment) {
        await handleAddComment(...args);
      }
      // trigger refetch of comments to update icon
      setCommentsRefetchKey((prev) => prev + 1);
    },
    [handleAddComment]
  );

  const customSubForms = React.useMemo(() => {
    const customs = [];
    if (hasHcpLicensesList) {
      customs.push({
        id: 'custom_hcp_licenses_list',
        name: 'License Information',
        isCustom: true,
        approved: isHcpLicensesListApproved ? 1 : 0,
      });
    }
    return customs;
  }, [hasHcpLicensesList, isHcpLicensesListApproved]);

  const profileCommentCountBySection = React.useMemo(() => {
    const m = new Map();
    for (const c of profileSectionComments) {
      const k = c.profile_section_key;
      if (!k) continue;
      m.set(k, (m.get(k) || 0) + 1);
    }
    return m;
  }, [profileSectionComments]);

  const approvedProfileSet = React.useMemo(
    () => new Set(approvedProfileSectionKeys),
    [approvedProfileSectionKeys]
  );

  const hcpProfileMenuFormEntries = React.useMemo(() => {
    if (
      !hcpProfileMenuEnabled ||
      !Array.isArray(hcpProfileMainMenuTabs) ||
      hcpProfileMainMenuTabs.length === 0
    ) {
      return [];
    }
    return hcpProfileMainMenuTabs.map((tab) => ({
      id: tab.id,
      name: tab.name,
      isHcpProfileMenu: true,
      approved: approvedProfileSet.has(tab.id) ? 1 : 0,
      hasComments: (profileCommentCountBySection.get(tab.id) || 0) > 0,
    }));
  }, [
    hcpProfileMenuEnabled,
    hcpProfileMainMenuTabs,
    approvedProfileSet,
    profileCommentCountBySection,
  ]);

  // License Information, then HCP profile (when enabled), then workflow subforms
  const allSubForms = React.useMemo(() => {
    return [...customSubForms, ...hcpProfileMenuFormEntries, ...subForms];
  }, [subForms, customSubForms, hcpProfileMenuFormEntries]);

  const [activeTab, setActiveTab] = useActiveTab(allSubForms, null, {
    preferHcpLicenseTab: hasHcpLicensesList,
  });

  // Use the hook for both fetching and actions (only for regular subforms)
  const {
    subForms: subFormsWithApproval,
    isCheckingApprovals,
    isApproving,
    isDeleting,
    approveSubForm,
    deleteApproval,
  } = useSubFormApproval({
    enduserFilledFormId: filledForm?.id,
    subForms: subForms, // only pass regular subforms, not custom ones
    persona: null,
  });

  const hcpLicensesListCommentsCount = hcpLicensesListComments.length;

  const allFormsWithApproval = React.useMemo(() => {
    const customFormsWithStatus = customSubForms.map((customForm) => ({
      ...customForm,
      approved:
        customForm.id === 'custom_hcp_licenses_list'
          ? isHcpLicensesListApproved
            ? 1
            : 0
          : 0,
      hasComments:
        customForm.id === 'custom_hcp_licenses_list'
          ? hcpLicensesListCommentsCount > 0
          : false,
    }));

    return [
      ...customFormsWithStatus,
      ...hcpProfileMenuFormEntries,
      ...subFormsWithApproval,
    ];
  }, [
    subFormsWithApproval,
    customSubForms,
    hcpProfileMenuFormEntries,
    isHcpLicensesListApproved,
    hcpLicensesListCommentsCount,
  ]);

  // Get the current tab's approval status from the updated forms
  const currentSubForm = allFormsWithApproval?.find((form) =>
    tabIdMatch(form.id, activeTab)
  );
  const isCurrentTabApproved = !!currentSubForm?.approved;
  const isHcpProfileMenuTab = currentSubForm?.isHcpProfileMenu === true;

  // Handle approve action
  const handleApprove = useCallback(async () => {
    if (!activeTab) return;

    if (isHcpProfileMenuTab) {
      if (!requestProgressId || !requestId) return;
      setProfileApproveBusy((b) => ({ ...b, approving: true }));
      let success = false;
      try {
        const response = await axiosInstance.post(
          '/api/approve-hcp-profile-section/approve',
          {
            request_progress_id: requestProgressId,
            request_id: requestId,
            profile_section_key: activeTab,
          }
        );
        success = response.status === 200 || response.status === 201;
        if (success) {
          setApprovedProfileSectionKeys((prev) =>
            prev.includes(activeTab) ? prev : [...prev, activeTab]
          );
        }
      } catch (e) {
        success = false;
      } finally {
        setProfileApproveBusy((b) => ({ ...b, approving: false }));
      }
      if (success) {
        toast.success('Section approved successfully!', {
          position: 'top-center',
        });
      } else {
        toast.error('Failed to approve section', {
          position: 'top-center',
        });
      }
      return;
    }

    let success = false;

    if (activeTab === 'custom_hcp_licenses_list') {
      success = await approveHcpLicensesList();
    } else {
      if (!filledForm?.id) return;
      success = await approveSubForm(activeTab);
    }

    if (success) {
      toast.success('Section approved successfully!', {
        position: 'top-center',
      });
    } else {
      toast.error('Failed to approve section', {
        position: 'top-center',
      });
    }
  }, [
    activeTab,
    filledForm?.id,
    approveSubForm,
    approveHcpLicensesList,
    isHcpProfileMenuTab,
    requestProgressId,
    requestId,
  ]);

  // Handle undo action
  const handleUndo = useCallback(async () => {
    if (!activeTab) return;

    if (isHcpProfileMenuTab) {
      if (!requestProgressId || !requestId) return;
      setProfileApproveBusy((b) => ({ ...b, deleting: true }));
      let success = false;
      try {
        const response = await axiosInstance.delete(
          '/api/approve-hcp-profile-section/delete',
          {
            data: {
              request_progress_id: requestProgressId,
              request_id: requestId,
              profile_section_key: activeTab,
            },
          }
        );
        success = response.status === 200;
        if (success) {
          setApprovedProfileSectionKeys((prev) =>
            prev.filter((k) => k !== activeTab)
          );
        }
      } catch (e) {
        success = false;
      } finally {
        setProfileApproveBusy((b) => ({ ...b, deleting: false }));
      }
      if (success) {
        toast.success('Section approval removed successfully!', {
          position: 'top-center',
        });
      } else {
        toast.error('Failed to remove approval', {
          position: 'top-center',
        });
      }
      return;
    }

    let success = false;

    if (activeTab === 'custom_hcp_licenses_list') {
      success = await deleteHcpLicensesListApproval();
    } else {
      if (!filledForm?.id) return;
      success = await deleteApproval(activeTab);
    }

    if (success) {
      toast.success('Section approval removed successfully!', {
        position: 'top-center',
      });
    } else {
      toast.error('Failed to remove approval', {
        position: 'top-center',
      });
    }
  }, [
    activeTab,
    filledForm?.id,
    deleteApproval,
    deleteHcpLicensesListApproval,
    isHcpProfileMenuTab,
    requestProgressId,
    requestId,
  ]);

  const canSubformApprove = isReviewerActionableStatus(requestProgress?.status);

  const {
    status: currentRequestStatus,
    requestProgress: currentRequestProgress,
  } = useCurrentStageStatus(requestId, persona);

  const canAddProfileSectionComments =
    canSubformApprove || isReviewerActionableStatus(currentRequestStatus);

  const profileCommentProgressId =
    canSubformApprove && requestProgressId
      ? requestProgressId
      : isReviewerActionableStatus(currentRequestStatus) &&
          currentRequestProgress?.id
        ? currentRequestProgress.id
        : requestProgressId;

  const approveBusy = isHcpProfileMenuTab
    ? profileApproveBusy.approving
    : isApproving;
  const deleteBusy = isHcpProfileMenuTab
    ? profileApproveBusy.deleting
    : isDeleting;

  return (
    <FormSectionsLayout
      forms={allFormsWithApproval}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      prevComments={[
        ...(prevComments || []),
        ...(profileSectionComments || []),
      ]}
      requestDetails={requestDetails}
      requestProgressId={requestProgressId}
      persona={persona}
      filledForm={filledForm}
      requestId={requestId}
      handleAddComment={handleAddCommentWrapper}
      enableAddComment={canSubformApprove}
      requestProgress={requestProgress}
      // Pass approval-related props
      formsWithApprovalStatus={allFormsWithApproval}
      isCheckingApprovals={isCheckingApprovals}
      skipApprovalFetch={true}
      hasHcpLicensesList={hasHcpLicensesList}
      isSendbackMode={isSendbackMode}
      roleName={roleName}
      // Pass approval handlers and state for custom forms
      customFormApprovalProps={{
        isApproved: isCurrentTabApproved,
        isApproving: approveBusy,
        isDeleting: deleteBusy,
        onApprove: canSubformApprove ? handleApprove : null,
        onUndo: canSubformApprove ? handleUndo : null,
        onCommentAdded: () => setCommentsRefetchKey((prev) => prev + 1),
      }}
      subformActionButtons={
        canSubformApprove && (
          <ApproveButton
            onApprove={handleApprove}
            onUndo={handleUndo}
            isApproving={approveBusy}
            isDeleting={deleteBusy}
            isApproved={isCurrentTabApproved}
            requestProgress={requestProgress}
            children={children}
          />
        )
      }
      formActionButtons={null}
      renderHcpProfileMenuContent={
        hcpProfileMenuEnabled &&
        typeof renderHcpProfileMenuContent === 'function'
          ? renderHcpProfileMenuContent
          : null
      }
      hcpProfileReviewerFooter={
        hcpProfileMenuEnabled &&
        persona !== 'enduser' &&
        isHcpProfileMenuTab ? (
          <HcpProfileSectionReviewerPanel
            persona={persona}
            activeSectionKey={activeTab}
            requestProgressId={requestProgressId}
            commentProgressId={profileCommentProgressId}
            requestId={requestId}
            sectionComments={profileSectionComments}
            onRefresh={async () => {
              setCommentsRefetchKey((k) => k + 1);
            }}
            enableAddComment={canAddProfileSectionComments}
          />
        ) : null
      }
    />
  );
};
